import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import type { SurveyQuestionDto, SurveyQuotaDimensionDto } from '@/shared/api/types'
import { saveQuotaDimension } from './api'
import { CODE_LIST_UNAVAILABLE, useCodes } from './useVocabulary'
import { count, quotaSourceLabel } from './vocab'

/** When each source can actually be enforced, which is the reason to pick one. */
const SOURCE_MEANING: Record<string, string> = {
  RESPONDENT_FIELD: 'Gender or age band as the register records it. Enforceable at the door, '
    + 'because the interviewer knows the answer before the interview starts.',
  QUESTION: 'Reported during the interview, so it is known too late to refuse anyone. Useful '
    + 'as a weighting margin, not as a gate.',
  GEOGRAPHY: 'Where the interview happened. Overlaps with strata — use one or the other for a '
    + 'given cut, not both.',
}

/**
 * The unit a cell's target is counted in, spelled out.
 *
 * Deliberately not `quotaBasisLabel`, which says "Count" and "Share" — the right
 * words in a table column headed "Basis", and the wrong ones in the select that
 * exists to stop somebody typing 30 without saying thirty of what.
 */
const BASIS_LABEL: Record<string, string> = {
  COUNT: 'Count of people',
  SHARE: 'Share of sample',
}

const basisLabel = (c: string) => BASIS_LABEL[c] ?? c

interface CellDraft {
  /** Local only. A server id would be wrong for a row that has not been saved yet. */
  key: string
  valueCode: string
  label: string
  /**
   * '' is the unmade choice, and it is a real state the server refuses.
   *
   * A plain string rather than a `'COUNT' | 'SHARE'` union: the list is served,
   * so a basis this build has never seen is a code the server considers legal and
   * a union here would make it unrepresentable — the cell would silently reset to
   * "unmade" on open, which is the same as losing it.
   */
  basisCode: string
  targetN: string
  targetShare: string
  /** From the server for an existing cell; null for a row somebody just added. */
  achieved: number | null
}

let draftSeq = 0
const newDraft = (): CellDraft => ({
  key: `draft-${draftSeq += 1}`, valueCode: '', label: '', basisCode: '',
  targetN: '', targetShare: '', achieved: null,
})

/** `age_band`, `ageBand` and `AGE BAND` all name the same register field. */
const normalise = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')

/**
 * Declaring a quota dimension and every cell in it, in one write.
 *
 * ## The one thing this editor exists to make impossible
 *
 * PollSphere stores a quota target as `30` in a JSON blob with nothing beside
 * it saying whether that means thirty people or thirty per cent. Nothing
 * downstream can read it, so nothing downstream does — the blob is written, is
 * never enforced, and contributes five points to a readiness score. The defect
 * is not that the number is wrong; it is that the number is **unreadable by
 * construction**, and no amount of care at the reading end can fix it.
 *
 * So `basisCode` is a required choice per cell with **no default**. An editor
 * that pre-selected COUNT would put the ambiguity back: everybody who meant a
 * share and typed 30 would get thirty interviews. The select opens on "Count or
 * share?" and stays there until somebody says.
 *
 * ## Value codes come from the server, never from this file
 *
 * `RESPONDENT_FIELD` reads its codes from `getRegisterVocabulary()` — the
 * register's own gender and age-band lists, so a quota on `A65_PLUS` and a
 * respondent field holding `A65_PLUS` are the same string by construction.
 * `QUESTION` reads them from the chosen question's options. Only `GEOGRAPHY`
 * is free text, because there is no bounded unit-code list to offer, and the
 * form says so rather than pretending.
 *
 * `QuotaSource` and `QuotaBasis` are both served by `GET /surveys/vocabulary`,
 * so neither picker below decides what exists. The English comes from
 * `vocab.tsx`; what is kept here is the one-line meanings, keyed by code. A
 * source the server adds appears in the select as its raw code rather than not
 * at all.
 */
export function QuotaDimensionDialog({
  open, surveyId, dimension, targetSample, register, questions, onClose, onSaved,
}: {
  open: boolean
  surveyId: string
  /** `null` is "add". */
  dimension: SurveyQuotaDimensionDto | null
  /** SHARE cells resolve against this. It is the poll's own target, not a guess. */
  /**
   * The poll's target sample, or null when it could not be read.
   *
   * Null rather than 0: a SHARE cell resolved against zero prints "Resolves to
   * 0" for every row — a target nobody set, stated with the same confidence as
   * one somebody did.
   */
  targetSample: number | null
  /** `getRegisterVocabulary()` — `{ gender: [...], ageBand: [...] }`, or null while it loads. */
  register: Record<string, string[]> | null
  /** The published instrument's questions, for a QUESTION-sourced dimension. */
  questions: SurveyQuestionDto[]
  onClose: () => void
  onSaved: () => void
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const sources = useCodes('quotaSource')
  const bases = useCodes('quotaBasis')
  const [sourceCode, setSourceCode] = useState('')
  const [questionId, setQuestionId] = useState('')
  const [registerField, setRegisterField] = useState('')
  const [cells, setCells] = useState<CellDraft[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const registerFields = useMemo(() => Object.keys(register ?? {}), [register])

  useEffect(() => {
    if (!open) return
    setCode(dimension?.code ?? '')
    setName(dimension?.name ?? '')
    setSourceCode(dimension?.sourceCode ?? '')
    setQuestionId(dimension?.questionId ?? '')
    setRegisterField(
      registerFields.find((f) => normalise(f) === normalise(dimension?.code ?? '')) ?? '')
    setCells((dimension?.cells ?? []).map((c) => ({
      key: c.id,
      valueCode: c.valueCode,
      label: c.label ?? '',
      basisCode: c.basisCode,
      targetN: c.targetN === null ? '' : String(c.targetN),
      targetShare: c.targetShare === null ? '' : String(c.targetShare),
      achieved: c.achieved,
    })))
    setError(null)
    // `registerFields` is derived from `register`, which arrives after the first
    // render; re-seeding on it is what makes the field guess land once it loads.
  }, [open, dimension, registerFields])

  const sourceMeaning = SOURCE_MEANING[sourceCode]
  const chosenQuestion = questions.find((q) => q.id === questionId)

  /**
   * The codes this dimension is allowed to quota on, from the server.
   *
   * Empty means "no list to offer" — GEOGRAPHY always, and QUESTION until a
   * question is picked — and the value column falls back to a text input with a
   * sentence saying why rather than an empty select nobody can use.
   */
  const valueCodes: string[] = sourceCode === 'RESPONDENT_FIELD'
    ? (register?.[registerField] ?? [])
    : sourceCode === 'QUESTION'
      ? (chosenQuestion?.options ?? []).map((o) => o.code)
      : []

  const optionLabel = (valueCode: string) =>
    (chosenQuestion?.options ?? []).find((o) => o.code === valueCode)?.label ?? valueCode

  const setCell = (key: string, patch: Partial<CellDraft>) =>
    setCells((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))

  const addEveryValue = () => setCells((rows) => {
    const have = new Set(rows.map((r) => r.valueCode))
    const added = valueCodes.filter((v) => !have.has(v)).map((v) => ({
      ...newDraft(),
      valueCode: v,
      label: sourceCode === 'QUESTION' ? optionLabel(v) : '',
    }))
    return [...rows, ...added]
  })

  /**
   * Arithmetic on the user's own inputs, not on survey data.
   *
   * Rule 2 exists so a client-computed figure cannot disagree with the
   * server-computed one beside it. Neither of these has a server counterpart on
   * this screen: the share total is a sum of numbers typed into this form, and
   * the resolved target is a preview of the multiplication the server is about
   * to do (`round(share × target sample)`) on those same numbers. The
   * authoritative `target` comes back on the saved cell and is what the Sample
   * page's table renders — this is the editor showing its work, so that the
   * count-or-share choice is visible in the units it will land in.
   */
  const shareTotal = cells
    .filter((c) => c.basisCode === 'SHARE' && c.targetShare.trim() !== '')
    .reduce((n, c) => n + (Number(c.targetShare) || 0), 0)

  const resolved = (c: CellDraft): number | null => {
    if (c.basisCode === 'COUNT') return c.targetN.trim() === '' ? null : Number(c.targetN)
    if (c.basisCode === 'SHARE') {
      if (c.targetShare.trim() === '' || targetSample === null) return null
      return Math.round(Number(c.targetShare) * targetSample)
    }
    return null
  }

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      await saveQuotaDimension(surveyId, {
        code: code.trim(),
        name: name.trim(),
        sourceCode,
        questionId: sourceCode === 'QUESTION' ? (questionId || null) : null,
        ...(dimension ? { position: dimension.position } : {}),
        cells: cells.map((c) => ({
          valueCode: c.valueCode.trim(),
          label: c.label.trim() === '' ? null : c.label.trim(),
          basisCode: c.basisCode,
          // A COUNT cell sends no share and a SHARE cell sends no count. Sending
          // both would leave the server choosing which one the author meant,
          // which is the ambiguity this whole dialog exists to remove.
          targetN: c.basisCode === 'COUNT' && c.targetN.trim() !== '' ? Number(c.targetN) : null,
          targetShare: c.basisCode === 'SHARE' && c.targetShare.trim() !== ''
            ? Number(c.targetShare) : null,
        })),
      })
      onSaved()
    } catch (e) {
      // Renders verbatim: shares over the whole, a dimension with no cells, a
      // COUNT cell with no targetN. Each names the cell and says what breaks.
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const unset = cells.filter((c) => c.basisCode === '').length

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="lg"
      title={dimension ? `Quota on ${dimension.name || dimension.code}` : 'New quota dimension'}
      description="Every cell states whether its target is a count of people or a share of the sample. There is no third answer and no default."
      footer={(
        <div className="flex items-center gap-2 w-full">
          <span className="text-erp-xs text-muted-foreground">
            {cells.length === 0
              ? 'No cells yet — a dimension with none enforces nothing, and the server says so.'
              : unset > 0
                ? `${unset} cell${unset === 1 ? '' : 's'} have not said count or share.`
                : `${cells.length} cell${cells.length === 1 ? '' : 's'}.`}
          </span>
          <span className="flex-1" />
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          {/*
            Saveable in every state, including the ones that will be refused. The
            refusals are the teaching: "shares add up to 1.0400" and "cell FEMALE
            is SHARE and has no targetShare" say more than a greyed button.
          */}
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy} onClick={submit}>
            {busy ? 'Saving…' : dimension ? 'Save dimension' : 'Add dimension'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" required
            hint={dimension
              ? undefined
              : 'Up to 32 characters. It is also the dimension code a population frame row rakes to, so keep it identical on both.'}>
            <input className="erp-input h-9 w-full font-mono text-erp-sm" value={code} maxLength={32}
              disabled={!!dimension}
              onChange={(e) => setCode(e.target.value)} placeholder="gender" />
          </Field>
          <Field label="Name" required hint="What it is called on the fieldwork screen.">
            <input className="erp-input h-9 w-full" value={name} maxLength={200}
              onChange={(e) => setName(e.target.value)} placeholder="Gender" />
          </Field>
        </div>

        {dimension && (
          // Rule 3: a disabled control has to say why, in text, next to it — a
          // title attribute on a disabled input is unreachable by keyboard.
          <p className="text-erp-xs text-muted-foreground -mt-1">
            The code is locked while editing because the server matches on it: saving under a
            different code creates a second dimension rather than renaming this one, and the
            population frame rows keyed to <span className="font-mono">{dimension.code}</span>{' '}
            would stay pointed at the old one.
          </p>
        )}

        <Field label="Source" required
          hint={sourceMeaning
            ?? 'Where a respondent\u2019s value for this dimension is read from \u2014 which '
              + 'decides whether the quota can be enforced at the door or only weighted for '
              + 'afterwards.'}
          error={sources.unavailable ? CODE_LIST_UNAVAILABLE : undefined}>
          <select className="erp-input h-9 w-full" value={sourceCode}
            disabled={sources.loading || sources.unavailable}
            onChange={(e) => { setSourceCode(e.target.value) }}>
            {/* No default, for the same reason the basis has none: a source
                nobody chose is a source nobody checked, and RESPONDENT_FIELD
                pre-selected would quietly become the answer on every dimension
                saved without opening this select. */}
            <option value="">
              {sources.loading ? 'Loading\u2026' : 'Where does the value come from?'}
            </option>
            {sources.codes.map((c) => (
              <option key={c} value={c}>{quotaSourceLabel(c)}</option>
            ))}
          </select>
        </Field>

        {sources.unavailable && (
          <Note tone="bad">
            A quota dimension cannot be saved without a source the server recognises, and the
            list of them could not be read.
            <button type="button" className="erp-btn-ghost h-7 text-erp-xs ml-2"
              onClick={() => sources.reload()}>
              Try again
            </button>
          </Note>
        )}

        {sourceCode === 'RESPONDENT_FIELD' && (
          <Field label="Register field" required
            hint="Which of the register's own lists the cell values come from, so a quota code and a respondent's recorded value are the same string.">
            <select className="erp-input h-9 w-full" value={registerField}
              onChange={(e) => setRegisterField(e.target.value)}>
              <option value="">Choose a field…</option>
              {registerFields.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
        )}

        {sourceCode === 'QUESTION' && (
          <Field label="Question"
            hint="Optional on the wire, but a QUESTION-sourced dimension with no question has nothing to read a respondent's value from.">
            <select className="erp-input h-9 w-full" value={questionId}
              onChange={(e) => setQuestionId(e.target.value)}>
              <option value="">Choose a question…</option>
              {questions.map((q) => (
                <option key={q.id} value={q.id}>{q.code} · {q.text}</option>
              ))}
            </select>
          </Field>
        )}

        {sourceCode === 'GEOGRAPHY' && (
          <Note tone="info">
            Geography value codes are typed by hand: there is no bounded list of unit codes to
            offer, and a dropdown built from one page of the unit tree would be a shorter list
            than the truth. If this cut is a place, a stratum is usually the better home for it —
            a stratum carries a population size, and a quota cell does not.
          </Note>
        )}

        <div className="pt-1">
          <div className="flex items-center justify-between gap-2 pb-2">
            <div className="min-w-0">
              <p className="text-erp-sm font-medium">Cells</p>
              <p className="text-erp-xs text-muted-foreground">
                One row per value this dimension quotas on.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {valueCodes.length > 0 && (
                <button type="button" className="erp-btn h-8 text-erp-xs" onClick={addEveryValue}>
                  Add every value
                </button>
              )}
              <button type="button" className="erp-btn h-8 text-erp-xs"
                onClick={() => setCells((r) => [...r, newDraft()])}>
                <Plus className="h-3 w-3" /> Add cell
              </button>
            </div>
          </div>

          <ErpTable head={(
            <>
              {/* Explicit widths: the dialog caps at max-w-3xl and, left to
                  auto layout, Value and Label starve the basis select until
                  "Share of the sample" reads as "Share of the s…". */}
              <Th className="w-36">Value</Th>
              <Th className="w-24">Label</Th>
              <Th className="w-48">Basis</Th>
              <Th className="text-right w-32">Target</Th>
              <Th className="text-right" title="What the server will store as this cell's target">
                Resolves to
              </Th>
              <Th className="text-right">Achieved</Th>
              <Th />
            </>
          )}>
            {cells.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-erp-sm text-muted-foreground">
                  No cells. Saving like this is refused — a dimension with no cells constrains
                  no interview and cannot be raked to.
                </td>
              </tr>
            ) : cells.map((c) => (
              <tr key={c.key} className="hover:bg-muted/40 align-top">
                <Td>
                  {valueCodes.length > 0 ? (
                    <select className="erp-input h-8 w-full text-erp-sm" value={c.valueCode}
                      onChange={(e) => setCell(c.key, {
                        valueCode: e.target.value,
                        label: c.label === '' && sourceCode === 'QUESTION'
                          ? optionLabel(e.target.value) : c.label,
                      })}>
                      <option value="">Choose…</option>
                      {valueCodes.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  ) : (
                    <input className="erp-input h-8 w-full font-mono text-erp-xs" maxLength={64}
                      value={c.valueCode} placeholder="valueCode"
                      onChange={(e) => setCell(c.key, { valueCode: e.target.value })} />
                  )}
                </Td>
                <Td>
                  <input className="erp-input h-8 w-full text-erp-sm" maxLength={200}
                    value={c.label} placeholder="optional"
                    onChange={(e) => setCell(c.key, { label: e.target.value })} />
                </Td>
                <Td>
                  <select className="erp-input h-8 w-full text-erp-sm" value={c.basisCode}
                    disabled={bases.loading || bases.unavailable}
                    onChange={(e) => setCell(c.key, { basisCode: e.target.value })}>
                    {/* No default. See the note at the top of this file. */}
                    <option value="">
                      {bases.loading ? 'Loading\u2026'
                        : bases.unavailable ? 'List unavailable' : 'Count or share?'}
                    </option>
                    {bases.codes.map((b) => (
                      <option key={b} value={b}>{basisLabel(b)}</option>
                    ))}
                  </select>
                </Td>
                <Td className="text-right">
                  {c.basisCode === 'COUNT' ? (
                    <input className="erp-input h-8 w-full text-right tabular-nums text-erp-sm"
                      type="number" min={0} value={c.targetN} placeholder="300"
                      onChange={(e) => setCell(c.key, { targetN: e.target.value })} />
                  ) : c.basisCode === 'SHARE' ? (
                    <input className="erp-input h-8 w-full text-right tabular-nums text-erp-sm"
                      type="number" min={0} max={1} step={0.001} value={c.targetShare}
                      placeholder="0.51"
                      onChange={(e) => setCell(c.key, { targetShare: e.target.value })} />
                  ) : c.basisCode === '' ? (
                    <span className="text-erp-xs text-muted-foreground">
                      say which first
                    </span>
                  ) : (
                    // A basis the server serves and this build has no field for.
                    // `QuotaCellRequest` carries exactly `targetN` and
                    // `targetShare`, so there is nothing to type — saying so
                    // beats an input that posts into neither.
                    <span className="text-erp-xs text-amber-700">
                      no field for {c.basisCode}
                    </span>
                  )}
                </Td>
                <Td className="text-right tabular-nums text-muted-foreground">
                  {resolved(c) === null ? '—' : count(resolved(c))}
                </Td>
                <Td className="text-right tabular-nums text-muted-foreground">
                  {c.achieved === null ? '—' : count(c.achieved)}
                </Td>
                <Td className="text-right">
                  <button type="button" className="erp-btn erp-btn-ghost h-7 w-7 p-0"
                    aria-label={`Remove cell ${c.valueCode || '(new)'}`}
                    onClick={() => setCells((rows) => rows.filter((r) => r.key !== c.key))}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </Td>
              </tr>
            ))}
          </ErpTable>

          <div className="pt-2 space-y-1.5 text-erp-xs text-muted-foreground">
            <p>
              A SHARE cell resolves against this poll's target sample of{' '}
              <span className="tabular-nums">{targetSample === null ? '—' : count(targetSample)}</span>; a COUNT cell is the
              number itself. Both end up as the same `target` field, which is why the basis has
              to be recorded rather than inferred.
            </p>
            {shareTotal > 0 && (
              <p className={shareTotal > 1.0001 ? 'text-red-700' : undefined}>
                SHARE cells total <span className="tabular-nums">{shareTotal.toFixed(4)}</span>
                {shareTotal > 1.0001
                  ? ' — over the whole sample. The server refuses this, because no fieldwork can satisfy it.'
                  : shareTotal < 0.9999
                    ? ' — under 1, so part of the sample falls outside every cell and is unconstrained.'
                    : '.'}
              </p>
            )}
            {anyFieldworkDone(cells) && (
              <p>
                Lowering a target below what a cell has already achieved does not delete
                interviews; it closes the cell, and the capture screen starts refusing new ones
                into it.
              </p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

/** Only worth warning about a retrospective target change when fieldwork has started. */
function anyFieldworkDone(cells: CellDraft[]): boolean {
  return cells.some((c) => (c.achieved ?? 0) > 0)
}
